import React, {
  useState, useCallback, useMemo,
} from 'react';
import { PropTypes } from 'prop-types';
import isEmail from 'validator/lib/isEmail';
import Select from './SelectWrapper/Select';
import ErrorMessageWithIcon from '~/components/forms/ErrorMessageWithIcon';

const emailToOption = (email) => ({ value: email.trim(), label: email.trim() });

const EmailRecipientSelect = ({
  id,
  name,
  value = [],
  type = 'cc',
  placeholder = 'Type an Email Addresses: example@getbrokerkit.com',
  onChange,
}) => {
  const { currentTeam } = Rails.helpers;
  const localStorageKey = useMemo(
    () => `${type}_emails_${currentTeam.id}`,
    [type, currentTeam.id],
  );

  const [error, setError] = useState('');

  const blockedDomains = useMemo(() => {
    const domains = process.env.BLOCKED_EMAIL_DOMAINS || '';
    return domains.split(',').map((d) => d.trim()).filter(Boolean);
  }, []);

  const isBlockedDomain = useCallback((email) => {
    if (!email || !isEmail(email)) return false;
    const domain = email.split('@')[1];
    return blockedDomains.some((blocked) => domain === blocked);
  }, [blockedDomains]);

  const [options, setOptions] = useState(() => {
    const savedEmails = localStorage.getItem(localStorageKey);
    return savedEmails
      ? savedEmails.split(',')
        .filter(Boolean)
        .map(emailToOption)
      : [];
  });

  const currentValue = useMemo(
    () => {
      if (Array.isArray(value)) {
        return value.map(emailToOption);
      }
      return value ? value.split(',').filter(Boolean).map(emailToOption) : [];
    },
    [value],
  );

  const handleChange = useCallback((selectedOptions) => {
    setError('');
    const validOptions = Array.isArray(selectedOptions) ? selectedOptions : [];

    if (validOptions.length > 50) {
      setError('Maximum 50 email addresses allowed');
      return;
    }

    const emailArray = validOptions.map((opt) => opt.value);
    onChange(emailArray);
  }, [onChange]);

  const isValidNewOption = useCallback(
    (email) => {
      if (!email || !isEmail(email)) return false;

      if (isBlockedDomain(email)) {
        setError('This email domain is reserved for internal routing. Please use a different email address.');
        return false;
      }

      if (currentValue.length >= 50) {
        setError('Maximum 50 email addresses allowed');
        return false;
      }

      setError('');
      return true;
    },
    [isBlockedDomain, currentValue],
  );

  const formatCreateLabel = useCallback((inputValue) => {
    if (!isEmail(inputValue)) return null;

    if (isBlockedDomain(inputValue)) return null;

    if (options.some((opt) => opt.value === inputValue.trim())) {
      return 'Select this email';
    }

    return `Add ${inputValue}`;
  }, [options, isBlockedDomain]);

  const handleCreateOption = useCallback((inputValue) => {
    if (!isValidNewOption(inputValue)) return;

    const trimmedEmail = inputValue.trim();
    const newOption = emailToOption(trimmedEmail);

    if (!options.some((opt) => opt.value === trimmedEmail)) {
      const newOptions = [...options, newOption];
      setOptions(newOptions);
      localStorage.setItem(
        localStorageKey,
        newOptions.map((opt) => opt.value).join(','),
      );
    }

    if (!currentValue.some((v) => v.value === trimmedEmail)) {
      handleChange([...currentValue, newOption]);
    }
  }, [options, currentValue, localStorageKey, handleChange, isValidNewOption]);

  return (
    <div>
      <Select
        isCreatable
        isClearable
        isSearchable
        isMulti
        id={id}
        name={name}
        options={options}
        value={currentValue}
        onChange={handleChange}
        onCreateOption={handleCreateOption}
        placeholder={placeholder}
        isValidNewOption={isValidNewOption}
        formatCreateLabel={formatCreateLabel}
        noOptionsMessage={() => 'Type an Email Address'}
        isInvalid={!!error}
      />
      {error && <ErrorMessageWithIcon message={error} />}
    </div>
  );
};

EmailRecipientSelect.propTypes = {
  id:          PropTypes.string,
  name:        PropTypes.string,
  type:        PropTypes.oneOf(['cc', 'bcc']),
  placeholder: PropTypes.string,
  onChange:    PropTypes.func,
  value:       PropTypes.arrayOf(PropTypes.string),
};

EmailRecipientSelect.defaultProps = {
  id:          '',
  name:        '',
  type:        'cc',
  placeholder: 'Type an Email Addresses: example@getbrokerkit.com',
  onChange:    () => {},
  value:       [],
};

export default EmailRecipientSelect;
